<template>
  <div
    v-if="toggle"
    :class="{
      info_block: true,
      info_style: infoType === 'info' || infoType === 'importer',
      warning_style: infoType === 'warning',
    }"
    :style="customStyle"
  >
    <div
      v-if="infoType !== 'warning' && isClosable"
      class="cancel-button pr-2 pt-1"
    >
      <button
        type="button"
        title="Close"
        aria-label="Close"
        class="close"
        @click="handleClose()"
      >
        <RsIcon
          class="fa-sm"
          icon="times"
        />
      </button>
    </div>
    <div :class="`grid-container pt-${paddingY ? paddingY : '3'} pb-${paddingY ? paddingY : '3'}`">
      <RsIcon
        v-if="infoType === 'custom' && customIcon"
        class="pl-3 far fa-2x icon"
        :style="`color: ${customIconColor};`"
        :icon="customIcon"
        :weight="customIconWeight"
      />
      <RsIcon
        v-else-if="infoType === 'info'"
        class="pl-3 color-purple far fa-2x icon"
        icon="info-circle"
      />
      <RsIcon
        v-else-if="infoType === 'warning'"
        class="pl-3 pt-1 color-yellow fas fa-3x icon"
        icon="exclamation-triangle"
      />
      <RsIcon
        v-else-if="infoType === 'importer'"
        class="pl-3 color-purple far fa-2x fa-solid"
        icon="hexagon-exclamation"
      />
      <div
        v-if="!customContent"
        class="pl-1 text"
        :class="{
          text: true,
          'pl-1': infoType === 'info' || infoType === 'importer',
          'pl-3': infoType === 'warning',
          'mid-enlarge-text': infoType === 'importer',
          enlarge_text: infoType !== 'info' && infoType !== 'importer' && enlargeText,
        }"
      >
        <div
          v-if="title"
          :class="{
            bold_text: infoType === 'info' || infoType === 'importer' || boldTitle,
          }"
        >
          {{ title }}
        </div>

        <span
          :class="type"
          v-html="$utils.decodeHtml(msg || '') "
        />
        <slot
          v-if="!msg"
          name="msg"
        />
      </div>
      <slot
        v-if="customContent"
        name="customContent"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsInfoBlock',

  props: {
    /**
       * This is will be the title of the info block
       */
    title: String,
    /**
       * This is will be the message that you want to display
       */
    msg: {
      type: String,
      default: '',
    },
    /**
       * Takes a string to assign appropirate whitespace class to the text
       * should either be multi or single - can be left empty to default to single
       */
    type: {
      type: String,
      default: 'single',
    },
    /**
       * Takes a string to assign appropirate type of message box
       * default value is info
       */
    infoType: {
      type: String,
      default: 'info',
    },

    /**
       * Takes a boolean to apply visibility close button for info message
       * default value is true
       */
    isClosable: {
      type: Boolean,
      default: true,
    },

    /**
     * The padding in y axis
     */
    paddingY: Number,
    /**
     * Custom Icon for info block
     * Use font awesome icon class
     */
    customIcon: {
      type: String,
      default: '',
    },

    /**
     * Custom Icon Color
     * Use valid CSS color code
     */
    customIconColor: {
      type: String,
      default: '',
    },

    /**
     * Custom Style for info block
     * Use valid inline CSS styles
     */
    customStyle: {
      type: String,
      default: '',
    },

    /**
     * Bold the title for info block
     * default true
     */
    boldTitle: {
      type: Boolean,
      default: true,
    },

    /**
     * Enlarge text when infoType !== 'info'
     * Use font awesome icon class
     */
    enlargeText: {
      type: Boolean,
      default: true,
    },

    /**
     * Use Slot and Display data however you want
     * Custom Title & Msg
     */
    customContent: {
      type: Boolean,
      default: false,
    },

    /** *
     * FontAwesome icon weights: far | fas | fal | fad
     */
    customIconWeight: {
      type: String as () => 'far' | 'fas' | 'fal' | 'fad',
      default: 'fal',
    },
  },
  data() {
    return {
      toggle: true,
    };
  },

  methods: {
    handleClose() {
      this.toggle = false;
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.info_block {
  box-sizing: border-box;
  border-radius: $input-radius;
  font-size: 0.75rem;
  height: auto;
}
.icon{
  margin: auto 0;
}
.info_style {
  background: rgba($info-purple,0.1);
  border: 1px solid $secondary-color;
  box-shadow: 4px $secondary-color;
}
.warning_style {
  background: rgba($warning-background, 0.8);
  border: 1px solid $warning;
  box-shadow: 4px $warning;
}
.grid-container {
  display:grid;
  grid-template-columns: 50px 1fr;
}
.color-purple {
  color: $secondary-color;
}
.color-yellow {
  color: $warning-icon;
}
.cancel-button {
  text-align: right;

  .close {
        position: relative;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        padding: 0;
        width: 22px;
        height: 22px;
        border-radius: $global-radius;
        color: $black;
        transition: opacity 0.2s ease;
        &:hover {
          opacity: 1;
          background: $extra-light-gray;
        }
      }
}
.text {
  color: #2D2255
}
.enlarge_text {
  font-size: 1rem;
  color: $warning-text;
}
.mid-enlarge-text {
  font-size: 14px;
  color: #2D2255;
}
.bold_text {
  font-weight: bold;
}
.multi {
  white-space: pre-line;
}
.single {
  white-space: normal;
  vertical-align: sub;
}
</style>
